import { API_BASE } from '../env'
import type { Verdict } from '../rules/types'
import type { DecisionSnapshot, OrderIntent } from '../types/snapshot'

/**
 * The ONLY file that knows the guardian API's paths. Everything here is same-origin and
 * carries the session as a bearer header — never a cookie, because inside cTrader Web the
 * plugin is a cross-site iframe and cookies are partitioned or dropped there.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail: string,
  ) {
    super(detail || title)
  }
}

export interface Me {
  traderId: number
  name: string
  ctidUserId: number | null
}

export interface Tier1Report {
  snapshotSequence: number
  isBlocked: boolean
  violations: string[]
}

export interface Tier2Response {
  verdict: Verdict
  snapshotSequence: number
  agrees: boolean | null
}

export interface Tier3Response {
  status: 'cleared' | 'blocked'
  verdict: Verdict
  snapshotSequence: number
  label: string
  clientOrderId: string
  replayed: boolean
}

export interface StartLogin {
  url: string
  state: string
  scope: string
}

/** The 200 body of /auth/claim. The status below is this client's, not the server's. */
export interface ClaimDone {
  token: string
  name: string
}

/** The 202 body: the daemon has not finished exchanging the code. */
export interface ClaimWaiting {
  status: string
  message: string
}

export type ClaimLogin =
  | { status: 'done'; token: string; name: string }
  | { status: 'waiting'; message: string }
  | { status: 'gone'; message: string }

// ── console shapes: what ConsoleEndpoints.cs serialises, camelCase ─────────

export type RuleMode = 'Observe' | 'Warn' | 'Enforce'

export interface TradingDay {
  anchorHour: number
  anchorZone: string
}

export interface AccountRow {
  account: number
  host: string
  isLive: boolean
  version: number
  maxRiskPercentPerTrade: number
  dailyLossLimit: number
  weeklyLossLimit: number
  maxConcurrentPositions: number
  tradingDay: TradingDay
  enforcing: number
  rules: number
}

export interface AccountsView {
  trader: string
  ctidUserId: number | null
  authorisationExpiresAt: string | null
  accounts: AccountRow[]
}

export interface DiscoveredAccount {
  accountId: number
  isLive: boolean
  host: string
  broker: string
  traderLogin: number | null
  balance: number | null
  leverage: number | null
}

export interface RuleExplanation {
  id: string
  title: string
  watches: string
  does: string
  why: string
}

export interface Template {
  name: string
  maxRiskPercentPerTrade: number
  maxConcurrentPositions: number
  maxOpenRiskPercent: number
  maxDrawdownPercent: number
  hedgingAllowed: boolean
  dailyLossLimit: number
  weeklyLossLimit: number
}

export interface Submitted {
  id: number
  kind: string
  status: string
}

export interface RequestStatus {
  id: number
  kind: string
  status: 'pending' | 'working' | 'done' | 'failed'
  result: Record<string, unknown> | null
  error: string | null
}

/** Mirror of Guardian.Core RuleSettings, camelCase. Passed back unchanged on a change. */
export interface RuleSettings {
  version: number
  preTrade: unknown[]
  maxRiskPercentPerTrade: number
  dailyLossLimit: number
  weeklyLossLimit: number
  defaultStopLossGrace: string
  maxConcurrentPositions: number
  maxStopDistancePercent: number
  maxOpenRiskPercent: number
  maxDrawdownPercent: number
  minMarginLevel: number
  hedgingAllowed: boolean
  defaultStopLossPercent: number
  reactiveModes: Record<string, RuleMode>
  maxSymbolExposurePercent: number
  peerApprovalThreshold: number
  relaxationDelay: string
  tradingDay: TradingDay
}

export interface PendingRuleChange {
  version: number
  effectiveFrom: string
  /** The column's own word, all the way to the screen. It was `author` here and `created_by`
   * everywhere else, and when the query moved into the repository the two stopped meeting —
   * the pending line rendered "· by" with nothing after it. */
  createdBy: string
  approvedBy: string | null
}

export interface RulesView {
  account: number
  settings: RuleSettings
  pending: PendingRuleChange[]
}

export interface ChangePlan {
  isRelaxation: boolean
  isDelayed: boolean
  reasons: string[]
  effectiveFrom: string
  summary: string
}

export interface Activity {
  opened: number
  closed: number
  pending: number
  ticks: number
  latencyP50: number
  latencyP90: number
  latencyMax: number
  latencySamples: number
}

export type Readiness = 'Inconclusive' | 'NeedsReview' | 'CandidateForEnforce'

export interface VerdictRow {
  rule: string
  fired: number
  targets: number
  days: number
  suppressed: number
  mode: RuleMode
  readiness: Readiness
  reason: string
}

export interface ReportView {
  account: number
  days: number
  since: string
  until: string
  activity: Activity
  entries: number
  isIdle: boolean
  verdicts: VerdictRow[]
}

export interface JournalSummary {
  key: string
  count: number
  wins: number
  net: number
  best: number
  worst: number
  winRate: number
  average: number
  medianHoldMinutes: number | null
}

export interface JournalTrade {
  positionId: number
  leg: number
  openedAt: string | null
  closedAt: string
  source: string
  symbol: number | null
  side: string | null
  volume: number | null
  entryPrice: number | null
  exitPrice: number | null
  stopAtOpen: number | null
  net: number
  isWin: boolean
  wasNaked: boolean
  guardianIntervened: boolean
  openedBeforeWeWatched: boolean
  heldMinutes: number | null
}

export interface JournalView {
  account: number
  days: number
  since: string
  until: string
  isEmpty: boolean
  overall: JournalSummary
  bySource: JournalSummary[]
  byProtection: JournalSummary[]
  byWeekday: JournalSummary[]
  trades: JournalTrade[]
}

export class ApiClient {
  #token: string | null

  constructor(token: string | null = null) {
    this.#token = token
  }

  get token(): string | null {
    return this.#token
  }

  withToken(token: string | null): ApiClient {
    return new ApiClient(token)
  }

  // ── auth ──────────────────────────────────────────────────────────────

  startLogin(scope: 'accounts' | 'trading'): Promise<StartLogin> {
    return this.#json<StartLogin>('POST', '/auth/start', { scope })
  }

  async claimLogin(state: string): Promise<ClaimLogin> {
    const response = await this.#send('POST', '/auth/claim', { state })
    if (response.status === 200) {
      const body = (await response.json()) as ClaimDone
      return { status: 'done', token: body.token, name: body.name }
    }
    if (response.status === 202) {
      const body = (await response.json()) as ClaimWaiting
      return { status: 'waiting', message: body.message }
    }
    const problem = await readProblem(response)
    return { status: 'gone', message: problem.detail }
  }

  async logout(): Promise<void> {
    await this.#send('POST', '/auth/logout')
  }

  me(): Promise<Me> {
    return this.#json<Me>('GET', '/me/')
  }

  /** What each rule watches and does. Served from Guardian.Core, never restated here. */
  ruleCatalogue(): Promise<RuleExplanation[]> {
    return this.#json<RuleExplanation[]>('GET', '/rules/catalogue')
  }

  // ── snapshot ──────────────────────────────────────────────────────────

  snapshot(account: number): Promise<DecisionSnapshot> {
    return this.#json<DecisionSnapshot>('GET', `/snapshot/?account=${account}`)
  }

  /** The raw response for the stream reader in stream.ts — headers, not EventSource. */
  openStream(account: number, signal: AbortSignal): Promise<Response> {
    return this.#send('GET', `/snapshot/stream?account=${account}`, undefined, signal)
  }

  // ── orders: Tier 2 confirms, Tier 3 records — the SDK sends ───────────

  validate(intent: OrderIntent, tier1: Tier1Report | null): Promise<Tier2Response> {
    return this.#json<Tier2Response>('POST', '/orders/validate', { intent, tier1 })
  }

  async submit(intent: OrderIntent, tier1: Tier1Report | null): Promise<Tier3Response> {
    const response = await this.#send('POST', '/orders/', { intent, tier1 })
    if (response.status === 200 || response.status === 422) {
      return (await response.json()) as Tier3Response
    }
    throw await readProblem(response)
  }

  // ── the console: the same loaders the TUI calls, as the trader ─────────

  accounts(): Promise<AccountsView> {
    return this.#json<AccountsView>('GET', '/me/accounts')
  }

  discovered(): Promise<DiscoveredAccount[]> {
    return this.#json<DiscoveredAccount[]>('GET', '/me/discovered')
  }

  templates(): Promise<Template[]> {
    return this.#json<Template[]>('GET', '/templates')
  }

  refreshAccounts(): Promise<Submitted> {
    return this.#json<Submitted>('POST', '/me/refresh')
  }

  guard(accountId: number, template: string, equity: number | null): Promise<Submitted> {
    return this.#json<Submitted>('POST', '/me/guard', { accountId, template, equity })
  }

  request(id: number): Promise<RequestStatus> {
    return this.#json<RequestStatus>('GET', `/me/requests/${id}`)
  }

  rules(account: number): Promise<RulesView> {
    return this.#json<RulesView>('GET', `/accounts/${account}/rules`)
  }

  previewChange(account: number, rules: RuleSettings): Promise<ChangePlan> {
    return this.#json<ChangePlan>('POST', `/accounts/${account}/changes/preview`, { rules })
  }

  submitChange(account: number, rules: RuleSettings): Promise<Submitted> {
    return this.#json<Submitted>('POST', `/accounts/${account}/changes`, { rules })
  }

  report(account: number, days: number): Promise<ReportView> {
    return this.#json<ReportView>('GET', `/accounts/${account}/report?days=${days}`)
  }

  journal(account: number, days: number): Promise<JournalView> {
    return this.#json<JournalView>('GET', `/accounts/${account}/journal?days=${days}`)
  }

  /** Fire-and-forget timing for the ergonomics gate. Never awaited on the critical path. */
  telemetry(event: string, data: Record<string, unknown>): void {
    void this.#send('POST', '/me/telemetry', { event, ...data }).catch(() => undefined)
  }

  // ── plumbing ──────────────────────────────────────────────────────────

  async #json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await this.#send(method, path, body)
    if (!response.ok) throw await readProblem(response)
    return (await response.json()) as T
  }

  #send(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (this.#token) headers['authorization'] = `Bearer ${this.#token}`
    if (body !== undefined) headers['content-type'] = 'application/json'
    return fetch(`${API_BASE}${path}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    })
  }
}

async function readProblem(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { title?: string; detail?: string }
    return new ApiError(response.status, body.title ?? `HTTP ${response.status}`, body.detail ?? '')
  } catch {
    return new ApiError(response.status, `HTTP ${response.status}`, '')
  }
}
